import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { IDataProviderFilter } from 'core-components/types'

import { checkEmptyFields, parseFilterToGroups, parseGroupsToFilter } from '../helpers'

import { CHANGE_TROTTLE_TIMER, DEFAULT_GROUP } from './constants'
import { GroupWrapper } from './GroupWrapper'
import { ESaveTriggerType, IDataProviderFilterProps, IGroup } from './models/IDataProviderFilter'

import styles from './DataProviderFilter.module.scss'

export const DataProviderFilter = ({
  className,
  value,
  onChange,
  editors,
  withoutGroups,
  headerContent,
  saveTrigger = ESaveTriggerType.onSaveButtonClick,
  allowCreateFields,
}: IDataProviderFilterProps) => {
  const [filterValue, setFilterValue] = useState<IDataProviderFilter>(value)
  const [group, setGroup] = useState<IGroup>(DEFAULT_GROUP)

  const isFirstRender = useRef<boolean>(true)

  const isFilterChanged = useMemo(() => {
    return JSON.stringify(filterValue) !== JSON.stringify(value)
  }, [filterValue, value])

  const isExistEmptyFields = useMemo(() => {
    return checkEmptyFields(filterValue)
  }, [filterValue])

  const saveFilter = (filterValue: IDataProviderFilter) => {
    const isSavedByChange =
      saveTrigger === ESaveTriggerType.onChange && !isExistEmptyFields && isFilterChanged
    const isSavedOnButtonClick = saveTrigger === ESaveTriggerType.onSaveButtonClick
    if (isSavedByChange || isSavedOnButtonClick) {
      onChange(filterValue)
    }
  }

  const onGroupChange = (groupValue: IGroup) => {
    setGroup(groupValue)
    const filterValue = parseGroupsToFilter(groupValue)
    setFilterValue(filterValue)
  }

  const resetFilterToDefault = () => {
    setFilterValue(value)
    const parsedGroups = parseFilterToGroups(value)
    setGroup(parsedGroups)
  }

  useEffect(() => {
    if (isFirstRender.current) {
      const parsedGroups = parseFilterToGroups(value)
      setGroup(parsedGroups)
      isFirstRender.current = false
    }
  }, [value])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (saveTrigger === ESaveTriggerType.onChange) {
        saveFilter(filterValue)
      }
    }, CHANGE_TROTTLE_TIMER)
    return () => {
      clearTimeout(handler)
    }
  }, [filterValue])

  return (
    <div className={className || ''}>
      <>
        {headerContent ? headerContent() : null}
        <GroupWrapper
          allowCreate={allowCreateFields}
          editors={editors}
          group={group}
          withoutGroups={withoutGroups}
          onGroupChange={(groupValue) => onGroupChange(groupValue)}
        />

        {saveTrigger !== ESaveTriggerType.onChange ? (
          <div className={styles.actions}>
            <div className={styles.actions__item}>
              <Tooltip tipContent={() => <div data-testid="saveTooltipContent">Save</div>}>
                <Button
                  __testId={'SaveButton'}
                  iconButton={'kbase kbase-save'}
                  disabled={!isFilterChanged || isExistEmptyFields}
                  onClick={() => saveFilter(filterValue)}
                />
              </Tooltip>
            </div>
            <div className={styles.actions__item}>
              <Tooltip tipContent={() => <div data-testid="cancelTooltipContent">Cancel</div>}>
                <Button
                  __testId={'CancelButton'}
                  className={styles.actions__item}
                  iconButton={'kbase kbase-close'}
                  color={'danger'}
                  onClick={() => resetFilterToDefault()}
                  disabled={!isFilterChanged}
                />
              </Tooltip>
            </div>
          </div>
        ) : null}
      </>
    </div>
  )
}
